import React, { useState } from 'react';
import styled from 'styled-components';
import { PageLayout, MainContent, ContentContainer } from '../../styles/SharedStyles';
import ExpertSidebar from '../../components/expert/ExpertSidebar';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  SelectChangeEvent,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmailIcon from '@mui/icons-material/Email';
import ChatIcon from '@mui/icons-material/Chat';
import HelpIcon from '@mui/icons-material/Help';
import { PageTitle } from '../../styles/SharedStyles';

interface SupportTicket {
  subject: string;
  category: string;
  description: string;
  email: string;
}

const SupportPage: React.FC = () => {
  const [ticket, setTicket] = useState<SupportTicket>({
    subject: '',
    category: '',
    description: '',
    email: '',
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement ticket submission to backend
    console.log('Support ticket:', ticket);
    setShowSuccess(true);
    setTicket({
      subject: '',
      category: '',
      description: '',
      email: '',
    });
  };

  const handleTextChange = (field: keyof SupportTicket) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setTicket(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleCategoryChange = (e: SelectChangeEvent<string>) => {
    setTicket(prev => ({
      ...prev,
      category: e.target.value,
    }));
  };

  return (
    <PageLayout>
      <ExpertSidebar />
      <MainContent>
        <ContentContainer>
          <PageTitle variant="h4" fontWeight={600} gutterBottom>Support Center</PageTitle>

          <Grid>
            <ContactCard>
              <CardIcon>
                <EmailIcon fontSize="large" />
              </CardIcon>
              <CardTitle>Email Support</CardTitle>
              <CardContent>
                <Typography>
                  support@triplink.vip
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Response within 24 hours
                </Typography>
              </CardContent>
            </ContactCard>

            <ContactCard>
              <CardIcon>
                <ChatIcon fontSize="large" />
              </CardIcon>
              <CardTitle>Live Chat</CardTitle>
              <CardContent>
                <Typography>
                  Available Mon-Fri
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  9 AM - 5 PM EST
                </Typography>
              </CardContent>
            </ContactCard>

            <ContactCard>
              <CardIcon>
                <HelpIcon fontSize="large" />
              </CardIcon>
              <CardTitle>Help Center</CardTitle>
              <CardContent>
                <Typography>
                  Browse our knowledge base
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Find quick answers to common questions
                </Typography>
              </CardContent>
            </ContactCard>
          </Grid>

          <Section>
            <SectionTitle>Frequently Asked Questions</SectionTitle>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>How do I set up my calendar availability?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Navigate to the Meet Settings page and click on "Calendar Settings". 
                  You can set your available hours and customize your meeting duration preferences.
                  Don't forget to sync your external calendar to avoid double bookings.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>How do I customize my trip link URL?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Go to the Collect Settings page where you can set your custom URL username.
                  Your unique link will be triplink.vip/your-username. Make sure to choose
                  a memorable and professional username.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>How do I manage my leads?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  The Leads page provides a comprehensive view of all your potential clients.
                  You can track their status, add notes, and manage their information.
                  Use the filters to sort and find specific leads quickly.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Section>

          <Section>
            <SectionTitle>Submit a Support Ticket</SectionTitle>
            <StyledPaper>
              <form onSubmit={handleSubmit}>
                <FormGrid>
                  <TextField
                    label="Subject"
                    value={ticket.subject}
                    onChange={handleTextChange('subject')}
                    required
                    fullWidth
                  />

                  <FormControl fullWidth required>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={ticket.category}
                      onChange={handleCategoryChange}
                      label="Category"
                    >
                      <MenuItem value="technical">Technical Issue</MenuItem>
                      <MenuItem value="billing">Billing Question</MenuItem>
                      <MenuItem value="feature">Feature Request</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label="Email"
                    type="email"
                    value={ticket.email}
                    onChange={handleTextChange('email')}
                    required
                    fullWidth
                  />

                  <TextField
                    label="Description"
                    multiline
                    rows={4}
                    value={ticket.description}
                    onChange={handleTextChange('description')}
                    required
                    fullWidth
                  />

                  <SubmitButton
                    variant="contained"
                    type="submit"
                    fullWidth
                  >
                    Submit Ticket
                  </SubmitButton>
                </FormGrid>
              </form>
            </StyledPaper>
          </Section>
        </ContentContainer>
      </MainContent>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)}>
          Support ticket submitted successfully!
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

const Title = styled.h1`
  font-size: 2rem;
  color: #2D3748;
  margin-bottom: 2rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const ContactCard = styled(Paper)`
  && {
    padding: 2rem;
    text-align: center;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    border-radius: 1rem;
  }
`;

const CardIcon = styled.div`
  color: #00A699;
  margin-bottom: 1rem;
`;

const CardTitle = styled(Typography)`
  && {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: #2D3748;
  }
`;

const CardContent = styled.div`
  text-align: center;
`;

const Section = styled.section`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: #2D3748;
  margin-bottom: 1.5rem;
`;

const StyledPaper = styled(Paper)`
  && {
    padding: 2rem;
    border-radius: 1rem;
  }
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const SubmitButton = styled(Button)`
  && {
    background-color: #00A699;
    color: white;
    padding: 0.75rem;
    font-size: 1rem;
    text-transform: none;
    
    &:hover {
      background-color: #008F86;
    }
  }
`;

export default SupportPage;
